import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Check for existing session on app load
  useEffect(() => {
    const checkAuthState = () => {
      try {
        const userSession = localStorage.getItem('userSession');
        if (userSession) {
          const sessionData = JSON.parse(userSession);
          
          // Check if session is still valid (not expired)
          if (sessionData?.loginTime) {
            const loginTime = new Date(sessionData.loginTime);
            const currentTime = new Date();
            const timeDifference = currentTime - loginTime;
            const hoursElapsed = timeDifference / (1000 * 60 * 60);
            
            // Session expires after 24 hours (or if rememberMe is false, after 8 hours)
            const sessionDuration = sessionData?.rememberMe ? 24 : 8;
            
            if (hoursElapsed < sessionDuration) {
              setUser(sessionData);
            } else {
              // Session expired, clear it
              localStorage.removeItem('userSession');
            }
          }
        }
      } catch (error) {
        console.error('Error checking auth state:', error);
        localStorage.removeItem('userSession');
      } finally {
        // Ensure loading is set to false after auth check
        setIsLoading(false);
      }
    };

    // Add a small delay to prevent flash of loading screen
    const timer = setTimeout(() => {
      checkAuthState();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Real shopper auth — mirrors charmsbeautycare's flow. POSTs credentials
  // to the dashboard's storefront-auth endpoint, stores returned token, and
  // hydrates the user object from the API response.
  const API_BASE    = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_STOREFRONT_API_URL) || 'https://dashboard.offloadhost.com';
  const CUSTOMER_ID = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_CUSTOMER_ID) || '';

  const login = async (credentials) => {
    try {
      setIsLoading(true);
      const r = await fetch(`${API_BASE}/storefront/${CUSTOMER_ID}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          email: credentials?.email,
          password: credentials?.password,
        }),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok || !data?.token) {
        const msg = data?.error
          || (r.status === 429 ? 'Too many attempts — please slow down.' : 'Invalid email or password.');
        return {
          success: false,
          error: msg,
          emailUnverified: !!data?.email_unverified,
          unverifiedEmail: data?.email || credentials?.email || '',
        };
      }
      // Same shape charms uses: { token, account, nonces }
      const fullName = [data.account?.first_name, data.account?.last_name].filter(Boolean).join(' ').trim();
      const sessionData = {
        token: data.token,
        email: data.account?.email || credentials?.email,
        name: data.account?.display_name || fullName || data.account?.full_name || (credentials?.email || '').split('@')[0],
        role: 'user',
        joinDate: data.account?.created_at || new Date().toISOString(),
        loginTime: new Date().toISOString(),
        rememberMe: credentials?.rememberMe || false,
      };
      localStorage.setItem('userSession', JSON.stringify(sessionData));
      setUser(sessionData);
      navigate('/user-account-dashboard');
      return { success: true };
    } catch (err) {
      console.error('Login error:', err);
      return { success: false, error: 'Network error — please check your connection.' };
    } finally {
      setIsLoading(false);
    }
  };

  const switchToUserView = () => {
    // Allow admin to temporarily view as user without logging out
    if (user?.role === 'admin') {
      const updatedSession = {
        ...user,
        activeView: 'user',
        originalRole: user?.role
      };
      localStorage.setItem('userSession', JSON.stringify(updatedSession));
      setUser(updatedSession);
      navigate('/user-account-dashboard');
    }
  };

  const switchToAdminView = () => {
    // Allow user with admin privileges to switch back to admin view
    if (user?.originalRole === 'admin' || user?.role === 'admin') {
      const updatedSession = {
        ...user,
        activeView: 'admin',
        role: user?.originalRole || user?.role
      };
      delete updatedSession?.activeView;
      localStorage.setItem('userSession', JSON.stringify(updatedSession));
      setUser(updatedSession);
      navigate('/admin-dashboard');
    }
  };

  const isInUserView = () => {
    return user?.activeView === 'user';
  };

  const canSwitchToAdminView = () => {
    return user?.role === 'admin' || user?.originalRole === 'admin';
  };

  const logout = () => {
    localStorage.removeItem('userSession');
    setUser(null);
    navigate('/user-login');
  };

  const isAuthenticated = () => {
    return user !== null;
  };

  const hasRole = (role) => {
    // When admin switches to user view, treat them as user for role checking
    if (user?.activeView === 'user' && role === 'user') {
      return true;
    }
    // When in admin view or no active view, check actual role
    if (user?.activeView === 'admin' || !user?.activeView) {
      return user?.role === role;
    }
    // Default role check
    return user?.role === role;
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated,
    hasRole,
    isLoading,
    switchToUserView,
    switchToAdminView,
    isInUserView,
    canSwitchToAdminView
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};